import { cn } from '@latitude/token-utils';
import type { ElementType } from 'react';

import { Box, type BoxProps } from './Box';
import {
  COL_SPAN,
  COL_START,
  ROW_SPAN,
  ROW_START,
  type GridTrackCount,
} from './grid-classes';

type GridItemOwnProps = {
  colSpan?: GridTrackCount;
  rowSpan?: GridTrackCount;
  colStart?: GridTrackCount;
  rowStart?: GridTrackCount;
};

// Public API (design.md scope: Stack/Inline/Grid/GridItem). Placement classes
// for a Grid child — kept as its own component (unlike StackItem, which was
// rejected) because grid placement classes are numerous and noisy in a diff
// (design.md D4).
export type GridItemProps<E extends ElementType = 'div'> = GridItemOwnProps &
  Omit<BoxProps<E>, keyof GridItemOwnProps>;

export const GridItem = <E extends ElementType = 'div'>({
  colSpan,
  rowSpan,
  colStart,
  rowStart,
  className,
  ...rest
}: GridItemProps<E>) => {
  return (
    // @ts-expect-error - see Center.tsx for why: TS can't reduce two
    // independently-computed Omit<ComponentPropsWithoutRef<E>, ...>
    // expressions to prove they're structurally identical when E is an
    // unresolved generic, even though GridItemProps<E> is defined in terms of
    // BoxProps<E>.
    <Box<E>
      className={cn(
        colSpan !== undefined && COL_SPAN[colSpan],
        rowSpan !== undefined && ROW_SPAN[rowSpan],
        colStart !== undefined && COL_START[colStart],
        rowStart !== undefined && ROW_START[rowStart],
        className,
      )}
      {...rest}
    />
  );
};

GridItem.displayName = 'GridItem';
